import React from 'react';

// #region Styles
/**
 * Styling constants for the Report Header component
 */

/** Container for the entire header section */
const headerContainerStyle = {
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb',
};

/** Main title styling - matches custom report builder (34px, fontWeight 600) */
const titleStyle = {
    fontSize: '34px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
    marginBottom: '8px',
};

/** Description text - matches custom report builder */
const descriptionStyle = {
    color: '#6b7280',
    marginBottom: '20px',
};

/** Container for controls (button and checkboxes) */
const controlsContainerStyle = {
    display: 'flex',
    alignItems: 'space-between',
};

// #endregion

// #region Components
/**
 * RunReportButton Component
 * Button for triggering report execution - matches custom report builder style
 */
const RunReportButton = ({ onClick, isLoading }) => (
    <button
        style={{
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#fff',
            backgroundColor: isLoading ? '#93c5fd' : '#3a82f6',
            border: 'none',
            borderRadius: '6px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
        }}
        onClick={onClick}
        disabled={isLoading}
    >
        {isLoading ? 'Running...' : 'Run Report'}
    </button>
);

/**
 * ReportCheckbox Component
 * Checkbox control with hover effects
 */
const ReportCheckbox = ({ label, checked, onChange }) => (
    <label
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#4b5563',
            cursor: 'pointer',
            userSelect: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
                accentColor: '#3b82f6',
            }}
        />
        <span>{label}</span>
    </label>
);

/**
 * ReportHeader Component
 * Displays a standardized header for report pages with title, run button, and optional controls
 * Styled to match the custom report builder design
 *
 * @param {Object} props
 * @param {string} props.title - The report title to display
 * @param {string} [props.description] - Optional description text
 * @param {Function} props.onRunReport - Callback function when "Run Report" button is clicked
 * @param {boolean} props.isLoading - Whether the report is currently loading
 * @param {boolean} [props.hideRunButton] - When true, hide the Run Report button (e.g. when rendered in parent toolbar)
 * @param {Array} [props.checkboxes] - Optional array of checkbox configurations
 *   Each checkbox object should contain:
 *   - label: string - The checkbox label text
 *   - checked: boolean - Whether the checkbox is checked
 *   - onChange: Function - Callback when checkbox state changes
 */
const ReportHeader = ({ title, description, onRunReport, isLoading, hideRunButton, checkboxes = [] }) => {
    return (
        <div style={headerContainerStyle}>
            {/* Report title - optional */}
            {title && <span style={titleStyle}>{title}</span>}
            {description && <p style={descriptionStyle}>{description}</p>}

            {/* Controls container */}
            <div style={controlsContainerStyle}>
                {/* Run report button - hidden when rendered in parent toolbar */}
                {!hideRunButton && <RunReportButton onClick={onRunReport} isLoading={isLoading} />}

                {/* Optional checkboxes */}
                {checkboxes.map((checkbox, index) => (
                    <ReportCheckbox
                        key={index}
                        label={checkbox.label}
                        checked={checkbox.checked}
                        onChange={checkbox.onChange}
                    />
                ))}
            </div>
        </div>
    );
};

// #endregion

export default ReportHeader;
